import { Server } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { useVelociraptorSettings } from '../../hooks'
import { formatUptime } from '../../services/velociraptor-settings'
import type { ServiceStatusResponse } from '../../types/velociraptor-settings'
import { SectionCard } from './SectionCard'
import { ServiceControlPanel } from './ServiceControlPanel'
import { StatusBadge } from './StatusBadge'

type Settings = ReturnType<typeof useVelociraptorSettings>

interface StatusInfoGridProps {
  status: ServiceStatusResponse
}

function StatusInfoGrid({ status }: StatusInfoGridProps): React.ReactElement {
  const { t } = useTranslation('admin')
  const lastCheck = new Date(status.lastCheckAt).toLocaleTimeString()
  const version = status.version ?? 'Unknown'
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <div>
        <span className="text-xs text-stone-500 dark:text-stone-400">
          {t('velociraptorSettings.status')}
        </span>
        <div className="mt-1">
          <StatusBadge status={status.status} />
        </div>
      </div>
      <div>
        <span className="text-xs text-stone-500 dark:text-stone-400">
          {t('velociraptorSettings.version')}
        </span>
        <p className="mt-1 font-mono text-sm text-stone-900 dark:text-stone-100">{version}</p>
      </div>
      <div>
        <span className="text-xs text-stone-500 dark:text-stone-400">
          {t('velociraptorSettings.uptime')}
        </span>
        <p className="mt-1 text-sm text-stone-900 dark:text-stone-100">
          {formatUptime(status.uptimeSeconds)}
        </p>
      </div>
      <div>
        <span className="text-xs text-stone-500 dark:text-stone-400">
          {t('velociraptorSettings.lastCheck')}
        </span>
        <p className="mt-1 text-sm text-stone-900 dark:text-stone-100">{lastCheck}</p>
      </div>
    </div>
  )
}

interface ServiceStatusCardProps {
  settings: Settings
}

export function ServiceStatusCard({ settings }: ServiceStatusCardProps): React.ReactElement {
  const { t } = useTranslation('admin')
  return (
    <SectionCard
      title={t('velociraptorSettings.serviceStatus')}
      description={t('velociraptorSettings.serviceStatusDesc')}
      icon={Server}
    >
      <div className="space-y-6">
        {settings.status && <StatusInfoGrid status={settings.status} />}
        <div className="border-t border-stone-200 pt-4 dark:border-stone-700">
          <ServiceControlPanel
            isRunning={settings.isRunning}
            serviceControl={settings.serviceControl}
            onRefresh={settings.refetchStatus}
            deploymentType={settings.config?.deploymentType ?? ''}
          />
        </div>
      </div>
    </SectionCard>
  )
}
